import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { defineStore } from 'pinia'

export interface TabSyncMessage {
    type: 'EVENT_CREATED' | 'EVENT_PUBLISHED' | 'EVENT_DELETED' | 'TAB_OPENED' | 'TAB_CLOSED' | 'PING' | 'PONG' | 'ROUTE_CHANGED'
    tabId: string
    timestamp: number
    routeName?: string // Track which route each tab is on
    payload?: any
}

export interface TabInfo {
    id: string
    timestamp: number
    routeName?: string
    isAwake?: boolean
}

const STORAGE_KEY = 'dn_tab_sync'
const STORAGE_VERSION_KEY = 'dn_tab_sync_version'
const ACTIVE_TABS_KEY = 'dn_active_tabs'
const CURRENT_STORAGE_VERSION = 3 // Version 1: old format (no isAwake), Version 2: with isAwake, Version 3: with routeName
const HEARTBEAT_INTERVAL_NORMAL = 15000 // 15 seconds (normal mode)
const HEARTBEAT_INTERVAL_FAST = 5000 // 5 seconds (when multiple tabs warning is visible)
const CLEANUP_INTERVAL_NORMAL = 15000 // 15 seconds (normal mode)
const CLEANUP_INTERVAL_FAST = 5000 // 5 seconds (fast mode for quicker detection)
const TAB_TIMEOUT = 10000 // Consider tab closed/offline after 10s of no heartbeat
const SLEEP_DETECTION_THRESHOLD = 30000 // Consider tab sleeping/offline after 30s of no user activity

// Module-level singleton state to prevent duplicate initialization even if main.ts runs twice
let isInitialized = false
let heartbeatInterval: number | null = null
let cleanupInterval: number | null = null
let activityMonitorInterval: number | null = null
let cleanupActivityListeners: (() => void) | null = null

// Simple unique ID generator (no external dependency)
function generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
}

export const useTabSync = defineStore('tabSync', () => {
    const currentTabId = ref<string>('')
    const activeTabs = ref<Map<string, TabInfo>>(new Map())
    const currentRouteName = ref<string>('')
    const lastActivityTime = ref<number>(Date.now()) // Track this tab's last user activity
    const isTabAwake = ref<boolean>(true) // Track if current tab is awake/online
    const hasMultipleTabs = ref(false)
    const currentHeartbeatInterval = ref(HEARTBEAT_INTERVAL_NORMAL)
    
    // Computed property for active tab count
    const activeTabCount = computed(() => activeTabs.value.size)
    
    let lastUIInteractionTime = Date.now() // Track last real user activity (click, keypress, etc.)

    /**
     * Update the last activity time for this tab.
     * Called when user interacts with the page (click, keypress, etc.)
     */
    function updateActivityTime() {
        lastUIInteractionTime = Date.now()
        lastActivityTime.value = Date.now()
        
        // Wake up the tab if it was sleeping
        if (!isTabAwake.value) {
            isTabAwake.value = true
        }
    }

    /**
     * Check if this tab is currently awake/online.
     * A tab is considered offline if it hasn't had user interaction for SLEEP_DETECTION_THRESHOLD ms.
     */
    function checkTabAwakeStatus(): boolean {
        const timeSinceLastActivity = Date.now() - lastUIInteractionTime
        const wasAwake = isTabAwake.value
        isTabAwake.value = timeSinceLastActivity < SLEEP_DETECTION_THRESHOLD
        
        return isTabAwake.value
    }

    /**
     * Check if a specific tab is awake based on its last heartbeat timestamp.
     */
    function isTabStillAwake(tabInfo: TabInfo): boolean {
        const timeSinceLastHeartbeat = Date.now() - tabInfo.timestamp
        return timeSinceLastHeartbeat < SLEEP_DETECTION_THRESHOLD
    }

    /**
     * Update the current route name and notify other tabs
     */
    function setCurrentRoute(routeName: string) {
        currentRouteName.value = routeName
        const currentTab = activeTabs.value.get(currentTabId.value)
        if (currentTab) {
            currentTab.routeName = routeName
            activeTabs.value.set(currentTabId.value, currentTab)
            updateActiveTabsList()
            broadcastMessage({ 
                type: 'ROUTE_CHANGED', 
                tabId: currentTabId.value, 
                timestamp: Date.now(),
                routeName 
            })
        }
    }

    function initializeTabId() {
        // Try to get existing tab ID from sessionStorage (persists only in current tab)
        let tabId = sessionStorage.getItem('dn_tab_id')
        if (!tabId) {
            tabId = generateTabId()
            sessionStorage.setItem('dn_tab_id', tabId)
        }
        currentTabId.value = tabId
        registerTab(tabId)
    }

    function registerTab(tabId: string) {
        activeTabs.value.set(tabId, {
            id: tabId,
            timestamp: Date.now(),
            routeName: currentRouteName.value,
            isAwake: true
        })
        updateActiveTabsList()
        broadcastMessage({ 
            type: 'TAB_OPENED', 
            tabId, 
            timestamp: Date.now(),
            routeName: currentRouteName.value
        })
    }

    function updateActiveTabsList() {
        // Only update storage if this tab is awake/online
        if (!isTabAwake.value) {
            return
        }

        const tabs = Array.from(activeTabs.value.values()).map(tabInfo => ({
            id: tabInfo.id,
            timestamp: tabInfo.timestamp,
            routeName: tabInfo.routeName,
            isAwake: isTabStillAwake(tabInfo)
        }))
        localStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify(tabs))
        localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_STORAGE_VERSION.toString())
        hasMultipleTabs.value = activeTabs.value.size > 1
    }

    function cleanupInactiveTabs() {
        const now = Date.now()
        let cleaned = false
        
        activeTabs.value.forEach((tabInfo, tabId) => {
            const inactiveTime = now - tabInfo.timestamp
            
            // Remove tabs that are completely offline (exceeded TAB_TIMEOUT)
            if (inactiveTime > TAB_TIMEOUT) {
                activeTabs.value.delete(tabId)
                cleaned = true
            }
            // Log sleeping tabs but don't remove them yet
            else if (inactiveTime > SLEEP_DETECTION_THRESHOLD) {
                // Tab is sleeping, but still within timeout
            }
        })
        
        if (cleaned) {
            updateActiveTabsList()
        }
    }

    /**
     * Check and migrate storage format if needed.
     * Version 1: Old format without isAwake property
     * Version 2: Format with isAwake property and version field
     * Version 3: Format with isAwake and routeName fields (current)
     */
    function migrateStorageIfNeeded() {
        try {
            const storedVersion = localStorage.getItem(STORAGE_VERSION_KEY)
            const currentVersion = storedVersion ? parseInt(storedVersion) : 1
            
            // If already on latest version, nothing to do
            if (currentVersion >= CURRENT_STORAGE_VERSION) {
                return
            }
            
            // Migration to V3 should already be handled by main.ts
            localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_STORAGE_VERSION.toString())
        } catch (error) {
            console.error('Failed to verify storage version:', error)
            // Clear everything if storage is corrupted
            try {
                localStorage.removeItem(ACTIVE_TABS_KEY)
                localStorage.removeItem(STORAGE_VERSION_KEY)
                localStorage.removeItem(STORAGE_KEY)
                localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_STORAGE_VERSION.toString())
                localStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify([]))
                console.warn(`⚠️ Cleared corrupted storage, initialized fresh v${CURRENT_STORAGE_VERSION}`)
            } catch (clearError) {
                console.error('Failed to recover from storage error:', clearError)
            }
        }
    }

    function loadActiveTabsFromStorage() {
        try {
            const stored = localStorage.getItem(ACTIVE_TABS_KEY)
            const storedVersion = localStorage.getItem(STORAGE_VERSION_KEY)
            const version = storedVersion ? parseInt(storedVersion) : 1
            
            // Only load if we're on the correct version (v3)
            if (version < CURRENT_STORAGE_VERSION) {
                localStorage.removeItem(ACTIVE_TABS_KEY)
                localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_STORAGE_VERSION.toString())
                localStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify([]))
                return
            }
            
            if (stored) {
                const tabs = JSON.parse(stored) as Array<TabInfo>
                const now = Date.now()
                
                tabs.forEach((tabInfo) => {
                    // Validate V3 structure - must have id, timestamp, and routeName properties
                    if (!tabInfo.id || typeof tabInfo.timestamp !== 'number') {
                        console.warn(`⚠️ Invalid tab info structure, skipping:`, tabInfo)
                        return
                    }
                    
                    // Only load tabs that are still online (within TAB_TIMEOUT)
                    if (now - tabInfo.timestamp < TAB_TIMEOUT) {
                        activeTabs.value.set(tabInfo.id, tabInfo)
                    }
                })
            }
        } catch (error) {
            console.error('Failed to load active tabs from storage:', error)
            // Clear corrupted storage and start fresh
            try {
                localStorage.removeItem(ACTIVE_TABS_KEY)
                localStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify([]))
                console.warn('⚠️ Cleared corrupted tabs storage')
            } catch (clearError) {
                console.error('Failed to clear corrupted storage:', clearError)
            }
        }
    }

    function broadcastMessage(message: TabSyncMessage) {
        // Don't broadcast if tab is sleeping
        if (!isTabAwake.value && message.type !== 'TAB_CLOSED') {
            return
        }

        // Use localStorage for cross-tab communication
        try {
            const event = {
                ...message,
                _timestamp: Date.now() // Prevent processing old messages
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify(event))
        } catch (error) {
            console.error('Failed to broadcast message via localStorage:', error)
        }
    }

    function handleBroadcastMessage(message: TabSyncMessage) {
        try {
            // Don't process our own messages
            if (message.tabId === currentTabId.value) return

            switch (message.type) {
            case 'TAB_OPENED':
            case 'PING':
                activeTabs.value.set(message.tabId, {
                    id: message.tabId,
                    timestamp: message.timestamp,
                    routeName: message.routeName,
                    isAwake: true
                })
                updateActiveTabsList()
                // Respond with PONG
                if (message.type === 'PING') {
                    broadcastMessage({ 
                        type: 'PONG', 
                        tabId: currentTabId.value, 
                        timestamp: Date.now(),
                        routeName: currentRouteName.value
                    })
                }
                break
                
            case 'PONG':
                activeTabs.value.set(message.tabId, {
                    id: message.tabId,
                    timestamp: message.timestamp,
                    routeName: message.routeName,
                    isAwake: true
                })
                updateActiveTabsList()
                break
            
            case 'ROUTE_CHANGED':
                const existingTab = activeTabs.value.get(message.tabId)
                if (existingTab) {
                    existingTab.routeName = message.routeName
                    existingTab.timestamp = message.timestamp
                    activeTabs.value.set(message.tabId, existingTab)
                    updateActiveTabsList()
                }
                break
                
            case 'TAB_CLOSED':
                activeTabs.value.delete(message.tabId)
                updateActiveTabsList()
                break
                
            case 'EVENT_CREATED':
            case 'EVENT_PUBLISHED':
            case 'EVENT_DELETED':
                // Emit custom events that components can listen to
                window.dispatchEvent(new CustomEvent('tab-sync', { detail: message }))
                break
            }
        } catch (error: any) {
            console.error('❌ Error handling broadcast message:', error?.message || error)
            // Don't crash - log and continue
        }
    }

    function handleStorageChange(event: StorageEvent) {
        if (event.key === STORAGE_KEY && event.newValue) {
            try {
                const message = JSON.parse(event.newValue) as TabSyncMessage & { _timestamp: number }
                
                // Ignore old messages (older than 2 seconds)
                if (Date.now() - message._timestamp < 2000) {
                    handleBroadcastMessage(message)
                }
            } catch (error) {
                console.error('Failed to parse storage sync message:', error)
            }
        } else if (event.key === ACTIVE_TABS_KEY && event.newValue) {
            // Another tab updated the active tabs list
            loadActiveTabsFromStorage()
        } else if (event.key === STORAGE_VERSION_KEY && event.newValue) {
            // Storage version was updated by another tab
            migrateStorageIfNeeded()
        }
    }

    function sendHeartbeat() {
        // Check if tab is still awake
        checkTabAwakeStatus()
        
        // Only update timestamp if tab is awake
        if (isTabAwake.value) {
            const currentTab = activeTabs.value.get(currentTabId.value)
            if (currentTab) {
                currentTab.timestamp = Date.now()
                activeTabs.value.set(currentTabId.value, currentTab)
            }
            updateActiveTabsList()
            
            // Ping more frequently when multiple tabs are active
            const pingChance = hasMultipleTabs.value ? 0.5 : 0.3
            if (Math.random() < pingChance) {
                broadcastMessage({ 
                    type: 'PING', 
                    tabId: currentTabId.value, 
                    timestamp: Date.now(),
                    routeName: currentRouteName.value
                })
            }
        }
    }

    function updateHeartbeatInterval() {
        const newInterval = hasMultipleTabs.value ? HEARTBEAT_INTERVAL_FAST : HEARTBEAT_INTERVAL_NORMAL
        const newCleanupInterval = hasMultipleTabs.value ? CLEANUP_INTERVAL_FAST : CLEANUP_INTERVAL_NORMAL
        
        // Only restart if the interval actually changed
        if (newInterval !== currentHeartbeatInterval.value) {
            currentHeartbeatInterval.value = newInterval
            
            // Clear existing intervals
            if (heartbeatInterval !== null) {
                clearInterval(heartbeatInterval)
            }
            if (cleanupInterval !== null) {
                clearInterval(cleanupInterval)
            }
            
            // Start new intervals with updated frequency
            heartbeatInterval = window.setInterval(sendHeartbeat, newInterval)
            cleanupInterval = window.setInterval(cleanupInactiveTabs, newCleanupInterval)
        }
    }

    function notifyEventCreated(eventId: number, eventTitle: string) {
        broadcastMessage({
            type: 'EVENT_CREATED',
            tabId: currentTabId.value,
            timestamp: Date.now(),
            payload: { eventId, eventTitle }
        })
    }

    function notifyEventPublished(eventId: number) {
        broadcastMessage({
            type: 'EVENT_PUBLISHED',
            tabId: currentTabId.value,
            timestamp: Date.now(),
            payload: { eventId }
        })
    }

    function notifyEventDeleted(eventId: number) {
        broadcastMessage({
            type: 'EVENT_DELETED',
            tabId: currentTabId.value,
            timestamp: Date.now(),
            payload: { eventId }
        })
    }

    function initialize() {
        if (isInitialized) {
            return
        }
        isInitialized = true

        // Check and migrate storage if needed (v1 → v2)
        migrateStorageIfNeeded()
        
        initializeTabId()
        loadActiveTabsFromStorage()

        // Set up localStorage event listener for cross-tab communication
        window.addEventListener('storage', handleStorageChange)

        // Track user activity (clicks, keypresses, touches)
        const activityHandler = () => updateActivityTime()
        document.addEventListener('click', activityHandler, { passive: true })
        document.addEventListener('keydown', activityHandler, { passive: true })
        document.addEventListener('touchstart', activityHandler, { passive: true })

        // Store cleanup function to remove activity listeners later
        cleanupActivityListeners = () => {
            document.removeEventListener('click', activityHandler)
            document.removeEventListener('keydown', activityHandler)
            document.removeEventListener('touchstart', activityHandler)
        }

        // Start heartbeat with initial interval
        heartbeatInterval = window.setInterval(sendHeartbeat, currentHeartbeatInterval.value)
        
        // Start cleanup interval
        cleanupInterval = window.setInterval(cleanupInactiveTabs, HEARTBEAT_INTERVAL_NORMAL * 2)

        // Monitor activity status every 10 seconds
        activityMonitorInterval = window.setInterval(() => {
            checkTabAwakeStatus()
        }, 10000)

        // Watch for changes in tab count to adjust heartbeat frequency
        watch(hasMultipleTabs, () => {
            updateHeartbeatInterval()
        })

        // Initial ping to discover other tabs
        setTimeout(() => {
            updateActivityTime() // Mark as active on initialization
            broadcastMessage({ type: 'PING', tabId: currentTabId.value, timestamp: Date.now() })
        }, 500)
    }

    function cleanup() {
        // Notify other tabs this tab is closing
        broadcastMessage({ type: 'TAB_CLOSED', tabId: currentTabId.value, timestamp: Date.now() })
        
        // Remove this tab from active list
        activeTabs.value.delete(currentTabId.value)
        updateActiveTabsList()

        // Clean up intervals
        if (heartbeatInterval) {
            clearInterval(heartbeatInterval)
        }
        if (cleanupInterval) {
            clearInterval(cleanupInterval)
        }
        if (activityMonitorInterval) {
            clearInterval(activityMonitorInterval)
        }

        // Clean up listeners
        window.removeEventListener('storage', handleStorageChange)
        if (cleanupActivityListeners) {
            cleanupActivityListeners()
        }
    }

    onMounted(() => {
        initialize()
    })

    onBeforeUnmount(() => {
        cleanup()
    })

    return {
        currentTabId,
        currentRouteName,
        activeTabs,
        activeTabCount, // Computed property for active tab count
        hasMultipleTabs,
        isTabAwake, // New: exposed to track if current tab is awake
        lastActivityTime, // New: exposed to track last activity
        setCurrentRoute, // New: exposed to set current route name
        notifyEventCreated,
        notifyEventPublished,
        notifyEventDeleted,
        updateActivityTime, // New: exposed for manual activity tracking
        checkTabAwakeStatus, // New: exposed to manually check awake status
        isTabStillAwake, // New: exposed to check if a specific tab is awake
        initialize,
        cleanup
    }
})
